/* i18n : chaînes servies par window.TL_I18N (layout/theme.liquid, depuis locales/*.json).
   Le littéral français reste en repli si le blob manque (script chargé hors thème). */
function tlT(k, fallback) { return (window.TL_I18N && window.TL_I18N[k]) || fallback; }

/* Tiloli Promotions hub — #17. Client-driven filter/sort/paginate over the REAL, server-rendered
   product cards (snippets/tiloli-card.liquid, via sections/promo-hub-list.liquid — one <li
   class="product-grid__item" data-pct data-brand data-devices data-l1 data-l2 data-l3 data-price>
   per product, wrapping the real `.tlc` card markup). This script does NOT build cards — it only
   reads the `data-*` attributes on each `<li>` to filter (show/hide), sort (reorder the `<li>`
   nodes in the DOM), paginate (reveal first N, load more on scroll), and to build the self-pruning
   facets (#facet-brand / #facet-device / #facet-category / #facet-promo). Selections + sort are
   reflected into the URL via history.pushState (read back on load for shareable/back-button state).

   Full-collection load (fix for the #17 250-cap): the server only renders the FIRST paginate page
   (Shopify caps `paginate` at 250) but the union `promotions` collection is far larger, so before
   filtering, init() fetches the remaining `?page=N` pages same-origin and appends their real
   server-rendered <li> cards (see loadRemainingPages). This is what surfaces a reseller's promos
   that live beyond page 1.

   #352 perf rework of that load:
   - The section now renders ONLY viewer-eligible cards (effective_pct > 0 in Liquid) and stamps
     data-total-pages on the grid, so page count is known up front — no more sequential probing
     until an empty page.
   - Remaining pages are fetched IN PARALLEL through the Section Rendering API (?section_id=…,
     id discovered at runtime from the wrapping .shopify-section node) — each response is just
     this section, not the full page with header/footer/carousel.
   - Progressive reveal: page 1's cards (already eligible-only) are shown IMMEDIATELY in server
     order while the remaining pages stream in; the full filter/sort/facet pipeline then runs
     once over the complete set. No more display:none over the grid for the whole load.

   Price hydration: the card's live B2B net price is hydrated by assets/tiloli-card.js, which on
   DOMContentLoaded does `document.querySelectorAll('.tl-price[data-cust]:not(.tl-price--ready)')`
   over the WHOLE document and batches one fetch for every matched variant, plus a MutationObserver
   that catches any later-added node. The first-page cards are found by that initial pass; the
   pages this script appends afterward arrive as `.tl-price` nodes the MutationObserver picks up, so
   they hydrate too. `hidden`/CSS visibility does not affect querySelectorAll, so cards revealed
   later (facet change, load-more) are ALREADY hydrated; no extra wiring was needed here.

   No framework, no deps. Pure logic (PROMO_BUCKETS / bucketFor / matches / compare) are
   module-internal/file-local helper functions — this script has no bundler or module system and
   runs as a classic deferred <script>, so nothing here is an ES module export. */

// #17 — bucket thresholds. PLACEHOLDER pending Jerome (#17): confirm exact tranches.
const PROMO_BUCKETS = [10, 20, 30, 40, 50]; // "≥N% and < next"; last is "≥50%"

function bucketFor(pct) {
  let label = null;
  for (let i = 0; i < PROMO_BUCKETS.length; i++) {
    const lo = PROMO_BUCKETS[i], hi = PROMO_BUCKETS[i + 1];
    if (pct >= lo && (hi === undefined || pct < hi)) label = hi === undefined ? `≥${lo}%` : `${lo}–${hi}%`;
  }
  return label; // null if below the smallest bucket
}

function matches(item, { buckets, brands, devices, categories, priceMin, priceMax } = {}) {
  if (!(item.pct > 0)) return false; // hide non-promo for this viewer
  if (buckets?.length && !buckets.includes(item.bucket)) return false;
  if (brands?.length && !brands.includes(item.brand)) return false;
  if (devices?.length && !(item.devices || []).some((d) => devices.includes(d))) return false;
  // item.l1 can carry multiple values (a product can carry multiple L1 tags), so it must be
  // matched with .some(), not .includes(). item.l2 is intentionally NOT matched here —
  // collectFacetValues() below only offers l1/l3 as category facet options, so l2 never appears
  // in `categories` and would never match anyway.
  if (categories?.length && !(categories.includes(item.l3) || (item.l1 || []).some((c) => categories.includes(c)))) return false;
  if (priceMin != null && item.price < priceMin) return false;
  if (priceMax != null && item.price > priceMax) return false;
  return true;
}

function compareFor(sort) {
  const by = {
    'date-desc': (a, b) => b.date - a.date,
    'date-asc': (a, b) => a.date - b.date,
    'promo-desc': (a, b) => b.pct - a.pct,
    'promo-asc': (a, b) => a.pct - b.pct,
    'price-asc': (a, b) => a.price - b.price,
    'price-desc': (a, b) => b.price - a.price,
  };
  return by[sort] || by['date-desc'];
}

// Recherche dans un filtre : comparaison insensible à la casse et aux accents
// ("ecouteur" trouve "Écouteurs"), espaces de bord retirés et espaces multiples réduits.
function normalizeFacetText(s) {
  let t = String(s == null ? '' : s);
  if (typeof t.normalize === 'function') t = t.normalize('NFD').replace(/[̀-ͯ]/g, '');
  return t.toLowerCase().replace(/\s+/g, ' ').trim();
}

// Une option du panneau reste visible si elle est cochée (toujours), si la requête est vide,
// ou si son libellé contient la requête. N'influe PAS sur les produits affichés.
function facetOptionVisible(label, query, checked) {
  if (checked) return true;
  const q = normalizeFacetText(query);
  return !q || normalizeFacetText(label).indexOf(q) !== -1;
}

/* ------------------------------------------------------------------------------------------- *
 * DOM wiring — everything below reads/writes the page. Guarded so a second script load is safe
 * (mirrors the convention in tiloli-card.js).
 * ------------------------------------------------------------------------------------------- */
if (!window.__tlPromoHubInit) {
  window.__tlPromoHubInit = true;

  const SECTION_SEL = '.promo-hub';
  const GRID_SEL = '#promo-hub-grid';
  const EMPTY_SEL = '#promo-hub-empty';
  const SENTINEL_SEL = '#promo-hub-sentinel';
  const SORT_SEL = '#promo-hub-sort-select';
  const COUNT_SEL = '#promo-hub-count';
  const FACET_LISTS = {
    brand: '#facet-brand',
    device: '#facet-device',
    category: '#facet-category',
    promo: '#facet-promo',
  };

  // ---- URL state (reflect selections; read back on load) --------------------------------------
  const STATE_KEYS = ['brand', 'device', 'category', 'promo', 'sort', 'priceMin', 'priceMax'];

  function readStateFromURL() {
    const params = new URLSearchParams(window.location.search);
    const state = {
      brand: params.has('brand') ? params.get('brand').split(',').filter(Boolean) : [],
      device: params.has('device') ? params.get('device').split(',').filter(Boolean) : [],
      category: params.has('category') ? params.get('category').split(',').filter(Boolean) : [],
      promo: params.has('promo') ? params.get('promo').split(',').filter(Boolean) : [],
      sort: params.get('sort') || 'date-desc',
      priceMin: params.has('priceMin') ? Number(params.get('priceMin')) : null,
      priceMax: params.has('priceMax') ? Number(params.get('priceMax')) : null,
    };
    return state;
  }

  function writeStateToURL(state) {
    const url = new URL(window.location.href);
    STATE_KEYS.forEach((k) => url.searchParams.delete(k));
    if (state.brand?.length) url.searchParams.set('brand', state.brand.join(','));
    if (state.device?.length) url.searchParams.set('device', state.device.join(','));
    if (state.category?.length) url.searchParams.set('category', state.category.join(','));
    if (state.promo?.length) url.searchParams.set('promo', state.promo.join(','));
    if (state.sort && state.sort !== 'date-desc') url.searchParams.set('sort', state.sort);
    if (state.priceMin != null) url.searchParams.set('priceMin', String(state.priceMin));
    if (state.priceMax != null) url.searchParams.set('priceMax', String(state.priceMax));
    history.pushState(state, '', url.toString());
  }

  // ---- Facet option lists (self-pruning: built ONLY from items with pct > 0) -------------------
  // A facet value appears only if at least one currently-promo-eligible (for this viewer)
  // product carries it — never an option with zero possible matches.
  function buildFacetSource(items) {
    return items.filter((it) => it.pct > 0);
  }

  function uniqueSorted(values) {
    return Array.from(new Set(values.filter((v) => v != null && v !== ''))).sort((a, b) =>
      String(a).localeCompare(String(b), 'fr')
    );
  }

  function collectFacetValues(promoItems) {
    const brands = uniqueSorted(promoItems.map((it) => it.brand));
    const devices = uniqueSorted(promoItems.flatMap((it) => it.devices || []));
    const categories = uniqueSorted(promoItems.flatMap((it) => [...(it.l1 || []), it.l3].filter(Boolean)));
    // Buckets: preserve PROMO_BUCKETS order (not alpha), self-pruned to buckets actually present.
    const bucketsPresent = new Set(promoItems.map((it) => it.bucket).filter(Boolean));
    const buckets = [];
    for (let i = 0; i < PROMO_BUCKETS.length; i++) {
      const lo = PROMO_BUCKETS[i], hi = PROMO_BUCKETS[i + 1];
      const label = hi === undefined ? `≥${lo}%` : `${lo}–${hi}%`;
      if (bucketsPresent.has(label)) buckets.push(label);
    }
    return { brands, devices, categories, buckets };
  }

  function renderFacetList(ul, values, dimension, selected, onChange) {
    if (!ul) return;
    ul.innerHTML = '';
    values.forEach((value) => {
      const li = document.createElement('li');
      li.className = 'facets__inputs-list-item';
      const id = `promo-hub-facet-${dimension}-${value}`.replace(/[^a-zA-Z0-9_-]/g, '-');
      const label = document.createElement('label');
      label.className = 'facets__label facets__input-label';
      label.setAttribute('for', id);
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.id = id;
      input.value = value;
      input.dataset.facetDimension = dimension;
      input.checked = selected.includes(value);
      input.addEventListener('change', onChange);
      label.appendChild(input);
      label.append(` ${value}`);
      li.appendChild(label);
      ul.appendChild(li);
    });
    // Facet with no eligible values: hide its panel entirely (self-pruning at the group level).
    const panel = ul.closest('.facets__panel');
    if (panel) panel.hidden = values.length === 0;
    setupFacetSearch(ul, values.length);
  }

  // ---- Recherche rapide dans un filtre long (> FACET_SEARCH_MIN options) ----------------------
  // Un <input type="search"> en tête du panneau masque (li.hidden) les options dont le libellé ne
  // contient pas la requête. Il ne filtre QUE la liste d'options : les produits restent filtrés par
  // les seules cases cochées (state), sur la liste complète. Les options cochées restent visibles.
  const FACET_SEARCH_MIN = 10;
  const FACET_SEARCH_DEBOUNCE_MS = 120;

  function facetSearchParts(ul) {
    const content = ul && ul.closest('.facets__panel-content');
    const box = content && content.querySelector(':scope > .promo-hub__facet-search');
    return {
      content,
      box,
      field: box && box.querySelector('input[type="search"]'),
      empty: ul && ul.parentNode && ul.parentNode.querySelector(':scope > .promo-hub__facet-search-empty'),
    };
  }

  // (Ré)applique la requête en cours à la <ul> — appelée à la saisie, après un re-rendu de la
  // liste, et en fin de updateFacetAvailability() (qui recoche / réordonne les options).
  function applyFacetSearch(ul) {
    if (!ul) return;
    const { field, empty } = facetSearchParts(ul);
    const query = field ? field.value : '';
    let matched = 0;
    ul.querySelectorAll(':scope > li').forEach((li) => {
      const input = li.querySelector('input[type="checkbox"]');
      const hit = facetOptionVisible(input ? input.value : li.textContent, query, false);
      const visible = hit || !!(input && input.checked); // cochée → toujours visible
      if (li.hidden === visible) li.hidden = !visible;
      if (hit) matched++;
    });
    // « Aucun résultat » = aucune option ne correspond à la requête (une cochée gardée visible n'en est pas une).
    if (empty) empty.hidden = matched > 0;
  }

  // Ré-applique la requête sur toutes les listes (les listes sans champ restent toutes visibles).
  function applyAllFacetSearches(section) {
    if (!section) return;
    Object.values(FACET_LISTS).forEach((sel) => applyFacetSearch(section.querySelector(sel)));
  }

  // Insère le champ (une seule fois) au-dessus de la <ul>, puis ré-applique la requête en cours.
  function setupFacetSearch(ul, count) {
    if (!ul) return;
    let { content, box, empty } = facetSearchParts(ul);
    if (!content) return;
    if (count <= FACET_SEARCH_MIN) {
      // Liste courte : pas de champ (et on retire celui d'un rendu précédent plus long).
      if (box) box.remove();
      if (empty) empty.remove();
      applyFacetSearch(ul);
      return;
    }
    if (!box) {
      box = document.createElement('div');
      // .tlf-search = même habillage que la recherche des filtres collection (tiloli-facets.css).
      box.className = 'tlf-search promo-hub__facet-search';
      const field = document.createElement('input');
      field.type = 'search';
      field.className = 'promo-hub__facet-search-input';
      field.placeholder = tlT('filterSearch', 'Rechercher…');
      field.setAttribute('aria-label', tlT('filterSearchLabel', 'Rechercher dans le filtre'));
      field.autocomplete = 'off';
      field.spellcheck = false;
      if (ul.id) field.setAttribute('aria-controls', ul.id);
      box.appendChild(field);
      content.insertBefore(box, content.firstChild);

      let timer = null;
      const applyNow = () => { clearTimeout(timer); applyFacetSearch(ul); };
      field.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => applyFacetSearch(ul), FACET_SEARCH_DEBOUNCE_MS);
      });
      // type="search" : la croix native (×) émet `search` → appliquer sans attendre.
      field.addEventListener('search', applyNow);
      field.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && field.value) {
          // Échap vide le champ sans fermer le panneau (le handler document le fermerait).
          e.preventDefault();
          e.stopPropagation();
          field.value = '';
          applyNow();
        } else if (e.key === 'Enter') {
          e.preventDefault(); // rien à soumettre : on garde le panneau tel quel
        }
      });
    }
    if (!empty && ul.parentNode) {
      empty = document.createElement('p');
      empty.className = 'promo-hub__facet-search-empty';
      empty.setAttribute('role', 'status');
      empty.hidden = true;
      empty.textContent = tlT('filterNoMatch', 'Aucun résultat');
      ul.parentNode.insertBefore(empty, ul.nextSibling);
    }
    applyFacetSearch(ul);
  }

  // ---- Reading the server-rendered <li> nodes ---------------------------------------------------
  // Each <li class="product-grid__item"> was emitted by sections/promo-hub-list.liquid wrapping
  // the REAL `{% render 'tiloli-card' %}` markup — this script never creates or mutates that
  // markup, it only reads the data-* attributes for filtering/sorting/faceting and toggles
  // `hidden` / reorders the <li> nodes themselves.
  function parseListItem(li) {
    let devices = [];
    try {
      devices = JSON.parse(li.dataset.devices || '[]') || [];
    } catch (e) {
      devices = [];
    }
    const l1 = (li.dataset.l1 || '').split('||').filter(Boolean);
    const l2 = (li.dataset.l2 || '').split('||').filter(Boolean);
    const pct = Number(li.dataset.pct) || 0;
    return {
      el: li,
      pct,
      bucket: bucketFor(pct),
      brand: li.dataset.brand || '',
      devices,
      l1,
      l2,
      l3: li.dataset.l3 || '',
      price: Number(li.dataset.price) || 0,
      date: Number(li.dataset.date) || 0,
    };
  }

  // ---- Load ALL paginate pages up front --------------------------------------------------------
  // Fix for the #17 "delivery ceiling": the `promotions` collection is the UNION of every group's
  // and customer's promos, so it far exceeds Shopify's 250-per-page `paginate` cap. The section
  // server-renders only the FIRST page, and a reseller's promos mostly live beyond it — so they
  // saw almost nothing. We fetch every subsequent page of THIS same page URL (?page=N), extract
  // their server-rendered <li> cards (already carrying the correct per-viewer data-pct, because
  // the fetch is same-origin + credentialed → Liquid sees the logged-in customer), and append them
  // so the filter/sort/facet logic below operates over the COMPLETE set.
  //
  // #352: page count comes from data-total-pages (stamped by the section from paginate.pages), so
  // all pages are fetched IN PARALLEL — wall clock ≈ the slowest single page instead of the sum.
  // Each fetch goes through the Section Rendering API (?section_id=<id>, discovered at runtime
  // from the wrapping .shopify-section node — theme/store-portable): the response is ONLY this
  // section's HTML, so the server skips the layout/header/footer/carousel render entirely.
  // Fallback: no data-total-pages (stale cached page 1) → the pre-#352 sequential probe.
  // Dedupe by variant id guards against a paginate clamp repeating the last page.
  const MAX_PAGES = 30; // safety backstop (30 × 250 = 7500 products)

  function collectPageDoc(doc, grid, seen) {
    if (!doc) return 0;
    const lis = doc.querySelectorAll('#promo-hub-grid > li.product-grid__item');
    const frag = document.createDocumentFragment();
    let added = 0;
    lis.forEach((li) => {
      const vid = li.querySelector('.tl-price')?.dataset.variantId;
      if (vid && seen.has(vid)) return; // dedupe: paginating past the last page repeats it
      if (vid) seen.add(vid);
      const node = document.importNode(li, true);
      // Arrive hidden: page 1's provisional chunk is already on screen (#352 progressive
      // reveal); streamed-in cards stay off until the final recompute() sorts/paginates.
      node.hidden = true;
      frag.appendChild(node);
      added++;
    });
    if (added) grid.appendChild(frag);
    return added;
  }

  async function fetchPageDoc(url) {
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return null;
      return new DOMParser().parseFromString(await res.text(), 'text/html');
    } catch (e) { return null; }
  }

  async function loadRemainingPages(grid) {
    const seen = new Set();
    grid.querySelectorAll(':scope > li').forEach((li) => {
      const vid = li.querySelector('.tl-price')?.dataset.variantId;
      if (vid) seen.add(vid);
    });
    const path = window.location.pathname; // no query — we want the full collection, unfiltered

    const totalPages = Math.min(Number(grid.dataset.totalPages) || 0, MAX_PAGES);
    if (totalPages) {
      if (totalPages <= 1) return;
      const sectionEl = grid.closest('.shopify-section');
      const sectionParam = sectionEl && sectionEl.id
        ? `&section_id=${encodeURIComponent(sectionEl.id.replace(/^shopify-section-/, ''))}`
        : '';
      const docs = [];
      for (let page = 2; page <= totalPages; page++) {
        docs.push(fetchPageDoc(`${path}?page=${page}${sectionParam}`));
      }
      // Promise.all preserves request order → cards append in page order even though the
      // fetches race. A failed page resolves null and is skipped (kept best-effort, like
      // the sequential loader's break-on-error).
      (await Promise.all(docs)).forEach((doc) => collectPageDoc(doc, grid, seen));
      return;
    }

    // Legacy sequential probe (no data-total-pages → section HTML predates #352).
    for (let page = 2; page <= MAX_PAGES; page++) {
      const doc = await fetchPageDoc(`${path}?page=${page}`);
      if (!doc) break;
      if (!collectPageDoc(doc, grid, seen)) break; // no new products on this page → done
    }
  }

  // ---- Main ------------------------------------------------------------------------------------
  async function init() {
    const section = document.querySelector(SECTION_SEL);
    if (!section) return;

    const grid = section.querySelector(GRID_SEL) || document.querySelector(GRID_SEL);
    const emptyEl = section.querySelector(EMPTY_SEL) || document.querySelector(EMPTY_SEL);
    const sentinel = section.querySelector(SENTINEL_SEL) || document.querySelector(SENTINEL_SEL);
    const sortSelect = section.querySelector(SORT_SEL) || document.querySelector(SORT_SEL);
    const countEl = section.querySelector(COUNT_SEL) || document.querySelector(COUNT_SEL);

    if (!grid) return;

    const productsPerPage = Number(section.dataset.productsPerPage) || 24;

    // Pull the remaining paginate pages BEFORE snapshotting so `items` covers the whole promo
    // collection, not just the first 250 the server rendered (see loadRemainingPages). The
    // empty-state decision below is made over the COMPLETE set — critical, since page 1 alone
    // can hold few/zero of a given reseller's promos and would otherwise short-circuit to
    // "no promotions".
    //
    // #352 progressive reveal: the section now renders ONLY viewer-eligible cards, so page 1
    // needs no hiding — show its first chunk IMMEDIATELY while the remaining pages stream in
    // behind a small loading line. The full filter/sort/facet pipeline below then recomputes
    // once over the complete set. (Pre-#352 this hid the whole grid behind display:none for
    // the entire sequential load — blank screen for many seconds.)
    //
    // The provisional chunk MUST respect URL state: a filtered deep link
    // (?brand=…&sort=…) would otherwise flash 24 arbitrary cards in server order for the
    // whole load window — the exact bug the old display:none guard existed to prevent.
    // matches() also rejects pct <= 0, covering stale-cached page-1 union cards.
    {
      const s = readStateFromURL();
      const firstItems = Array.from(grid.querySelectorAll(':scope > li')).map(parseListItem);
      const provisional = firstItems
        .filter((it) => matches(it, {
          buckets: s.promo, brands: s.brand, devices: s.device,
          categories: s.category, priceMin: s.priceMin, priceMax: s.priceMax,
        }))
        .sort(compareFor(s.sort));
      firstItems.forEach((it) => { it.el.hidden = true; });
      const frag = document.createDocumentFragment();
      provisional.forEach((it) => frag.appendChild(it.el));
      grid.appendChild(frag); // reorder page-1 nodes to the provisional sort
      provisional.slice(0, productsPerPage).forEach((it) => { it.el.hidden = false; });
    }
    const loadingEl = document.createElement('p');
    loadingEl.className = 'promo-hub__loading';
    loadingEl.setAttribute('aria-live', 'polite');
    loadingEl.textContent = tlT('promoLoading', 'Chargement des promotions…');
    grid.parentNode.insertBefore(loadingEl, grid.nextSibling);
    try { await loadRemainingPages(grid); } catch (e) { /* keep whatever pages we got */ }
    loadingEl.remove();

    // Snapshot the server-rendered <li> nodes ONCE (not per-render, not per-page) and compute
    // their filter/sort metadata from the data-* attributes emitted by the section.
    const items = Array.from(grid.querySelectorAll(':scope > li')).map(parseListItem);

    // Hide every non-promo-for-this-viewer card up front (pct <= 0 is always hidden, regardless
    // of filters) — this narrows the union `promotions` collection down to what THIS viewer
    // actually qualifies for (reseller-negotiated promos are per-customer).
    items.forEach((it) => {
      if (!(it.pct > 0)) it.el.hidden = true;
    });

    const promoEligible = buildFacetSource(items);
    const facetValues = collectFacetValues(promoEligible);

    // Global empty state: zero items have pct > 0 for this viewer regardless of filters.
    if (promoEligible.length === 0) {
      if (emptyEl) emptyEl.hidden = false;
      grid.hidden = true;
      const facetsBar = section.querySelector('.promo-hub__facets');
      if (facetsBar) facetsBar.hidden = true;
      const sortWrap = section.querySelector('#promo-hub-sort');
      if (sortWrap) sortWrap.hidden = true;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    grid.hidden = false;

    const initialState = readStateFromURL();
    const state = {
      brand: initialState.brand.filter((v) => facetValues.brands.includes(v)),
      device: initialState.device.filter((v) => facetValues.devices.includes(v)),
      category: initialState.category.filter((v) => facetValues.categories.includes(v)),
      promo: initialState.promo.filter((v) => facetValues.buckets.includes(v)),
      sort: initialState.sort,
      priceMin: initialState.priceMin,
      priceMax: initialState.priceMax,
    };

    if (sortSelect) sortSelect.value = state.sort;

    let renderedCount = 0;
    let currentMatches = [];
    let io = null;

    function currentOpts() {
      return {
        buckets: state.promo,
        brands: state.brand,
        devices: state.device,
        categories: state.category,
        priceMin: state.priceMin,
        priceMax: state.priceMax,
      };
    }

    // Recomputes over the FULL `items` array (global — not just the currently-visible chunk)
    // every time filters/sort change: hides everything, then reveals the first page via
    // revealNextChunk(), which moves each revealed chunk into sorted position in the DOM.
    function recompute() {
      currentMatches = items.filter((it) => matches(it, currentOpts())).sort(compareFor(state.sort));
      renderedCount = 0;
      if (countEl) countEl.textContent = String(currentMatches.length);

      // Hide all cards — the actual server-rendered <li> nodes (not rebuilt) so the real `.tlc`
      // markup/price/state inside each is never touched, just moved and shown/hidden.
      // Perf: only write `hidden` when it changes — almost every card is already hidden, and an
      // unconditional write still sets the attribute (~2,300 mutation records per filter click).
      items.forEach((it) => { if (!it.el.hidden) it.el.hidden = true; });
      // Perf: matches are NOT all re-appended here any more (up to ~2,300 node moves per click,
      // each also re-scanned by tiloli-card.js's MutationObserver) — revealNextChunk() moves only
      // the chunk it reveals. `currentMatches` still covers the WHOLE list (count, scroll reveal).

      updateFacetAvailability();
      renderActiveChips();
      revealNextChunk();
    }

    // ---- Filtres actifs : chips supprimables (style natif .facets-remove) --------------------
    const activeWrap = section.querySelector('[data-promo-hub-active]');
    const X_ICON = '<span class="svg-wrapper" aria-hidden="true"><svg viewBox="0 0 12 12" width="11" height="11"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/></svg></span>';

    function removeValue(dim, value) {
      state[dim] = (state[dim] || []).filter((v) => v !== value);
      // Perf: the facet inputs live in their FACET_LISTS <ul> — search there, not the whole
      // section (which also holds every product card, ~137k nodes).
      (section.querySelector(FACET_LISTS[dim]) || section).querySelectorAll('input[data-facet-dimension="' + dim + '"]').forEach((i) => {
        if (i.value === value) i.checked = false;
      });
      writeStateToURL(state);
      recompute();
    }

    function clearAllFilters() {
      state.brand = []; state.device = []; state.category = []; state.promo = [];
      state.priceMin = null; state.priceMax = null;
      section.querySelectorAll('input[data-facet-dimension]').forEach((i) => { i.checked = false; });
      writeStateToURL(state);
      recompute();
    }

    function renderActiveChips() {
      if (!activeWrap) return;
      const chips = [];
      [['brand', state.brand], ['device', state.device], ['category', state.category], ['promo', state.promo]]
        .forEach(([dim, vals]) => (vals || []).forEach((v) => chips.push({ dim: dim, value: v })));

      if (chips.length === 0) {
        activeWrap.hidden = true;
        activeWrap.innerHTML = '';
        return;
      }
      activeWrap.hidden = false;
      activeWrap.innerHTML = '';

      const count = document.createElement('span');
      count.className = 'facets-remove__count';
      count.textContent = chips.length + (chips.length > 1 ? ' filtres' : ' filtre');
      activeWrap.appendChild(count);

      chips.forEach((c) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'facets-remove__pill';
        b.setAttribute('aria-label', tlT('removeValue', 'Retirer [VALUE]').replace('[VALUE]', c.value));
        b.appendChild(document.createTextNode(c.value + ' '));
        b.insertAdjacentHTML('beforeend', X_ICON);
        b.addEventListener('click', () => removeValue(c.dim, c.value));
        activeWrap.appendChild(b);
      });

      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'facets__clear-all-link';
      clear.textContent = 'Tout effacer';
      clear.addEventListener('click', clearAllFilters);
      activeWrap.appendChild(clear);
    }

    // ---- Co-disponibilité : grise les options qui donneraient 0 résultat vu la sélection --------
    // Pour chaque dimension, on filtre par les AUTRES dimensions (on ignore la sienne), on collecte
    // les valeurs encore présentes, et on désactive celles qui manquent (les cochées restent
    // actives pour pouvoir les décocher).
    const DIM_OPTKEY = { brand: 'brands', device: 'devices', category: 'categories', promo: 'buckets' };
    function valuesForDim(it, dim) {
      if (dim === 'brand') return [it.brand];
      if (dim === 'device') return it.devices || [];
      if (dim === 'category') return [...(it.l1 || []), it.l3].filter(Boolean);
      if (dim === 'promo') return [it.bucket];
      return [];
    }
    function updateFacetAvailability() {
      ['brand', 'device', 'category', 'promo'].forEach((dim) => {
        const opts = currentOpts();
        opts[DIM_OPTKEY[dim]] = []; // ignorer la sélection de CETTE dimension
        const avail = new Set();
        items.forEach((it) => {
          if (matches(it, opts)) valuesForDim(it, dim).forEach((v) => avail.add(v));
        });
        // Perf : on cherche les inputs dans la <ul> de CETTE dimension, pas dans toute la section
        // (~137k nœuds avec les cartes produit), et on n'écrit `disabled` que s'il change
        // (classList.toggle(…, force) est déjà sans effet quand l'état ne change pas).
        const ul = section.querySelector(FACET_LISTS[dim]);
        (ul || section).querySelectorAll('input[data-facet-dimension="' + dim + '"]').forEach((input) => {
          const enabled = input.checked || avail.has(input.value);
          if (input.disabled !== !enabled) input.disabled = !enabled;
          const li = input.closest('.facets__inputs-list-item');
          if (li) li.classList.toggle('is-disabled', !enabled);
        });
        // Réordonner : options dispo d'abord, grisées ensuite (tri stable → alpha conservé par groupe).
        if (ul) {
          Array.from(ul.querySelectorAll(':scope > li'))
            .sort((a, b) => (a.classList.contains('is-disabled') ? 1 : 0) - (b.classList.contains('is-disabled') ? 1 : 0))
            .forEach((li) => ul.appendChild(li));
        }
      });
      applyAllFacetSearches(section); // recherche dans les filtres : garder la requête cohérente
    }

    // Paginate = reveal the next N already-in-DOM nodes (unhide), not create new ones. Because
    // every card is server-rendered and already hydrated by tiloli-card.js's DOMContentLoaded
    // pass (see file header), simply clearing `hidden` is enough — no re-hydration call needed.
    // Order: the chunk's <li> are appended, in sort order, to the END of the grid, i.e. after the
    // chunks already shown (the sentinel sits outside the <ul>). recompute() hid every card and
    // hidden <li> take no grid space, so what is on screen is exactly
    // currentMatches[0 … renderedCount) in sort order — without moving every match per click.
    function revealNextChunk() {
      const next = currentMatches.slice(renderedCount, renderedCount + productsPerPage);
      if (next.length) {
        const frag = document.createDocumentFragment();
        next.forEach((it) => frag.appendChild(it.el));
        grid.appendChild(frag);
      }
      next.forEach((it) => { it.el.hidden = false; });
      renderedCount += next.length;
      if (sentinel) sentinel.hidden = renderedCount >= currentMatches.length;
    }

    function onFacetChange(e) {
      const input = e.target;
      const dimension = input.dataset.facetDimension;
      const key = dimension === 'promo' ? 'promo' : dimension;
      const set = new Set(state[key]);
      if (input.checked) set.add(input.value);
      else set.delete(input.value);
      state[key] = Array.from(set);
      writeStateToURL(state);
      recompute();
    }

    renderFacetList(section.querySelector(FACET_LISTS.brand), facetValues.brands, 'brand', state.brand, onFacetChange);
    renderFacetList(section.querySelector(FACET_LISTS.device), facetValues.devices, 'device', state.device, onFacetChange);
    renderFacetList(section.querySelector(FACET_LISTS.category), facetValues.categories, 'category', state.category, onFacetChange);
    renderFacetList(section.querySelector(FACET_LISTS.promo), facetValues.buckets, 'promo', state.promo, onFacetChange);

    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        state.sort = sortSelect.value;
        writeStateToURL(state);
        recompute();
      });
    }

    // Client pagination: IntersectionObserver on the sentinel reveals the next chunk of the
    // already-computed (full, sorted, filtered) `currentMatches` array.
    if (sentinel && 'IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) revealNextChunk();
      });
      io.observe(sentinel);
    }

    // Back/forward navigation: re-read the URL and re-render.
    window.addEventListener('popstate', () => {
      const s = readStateFromURL();
      state.brand = s.brand.filter((v) => facetValues.brands.includes(v));
      state.device = s.device.filter((v) => facetValues.devices.includes(v));
      state.category = s.category.filter((v) => facetValues.categories.includes(v));
      state.promo = s.promo.filter((v) => facetValues.buckets.includes(v));
      state.sort = s.sort;
      state.priceMin = s.priceMin;
      state.priceMax = s.priceMax;
      if (sortSelect) sortSelect.value = state.sort;
      section.querySelectorAll('input[type="checkbox"][data-facet-dimension]').forEach((input) => {
        const dim = input.dataset.facetDimension;
        input.checked = state[dim]?.includes(input.value) || false;
      });
      recompute();
    });

    // Fermeture des filtres au clic extérieur / Échap (les <details> ne se ferment pas seuls,
    // contrairement au comportement des facettes des autres pages via tiloli-facets).
    document.addEventListener('click', (e) => {
      section.querySelectorAll('.facets__panel[open]').forEach((panel) => {
        if (!panel.contains(e.target)) panel.open = false;
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        section.querySelectorAll('.facets__panel[open]').forEach((panel) => { panel.open = false; });
      }
    });

    recompute();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
